<template>
  <div class="domain-picker-modal" @click.self="$emit('close')">
    <div class="domain-picker-modal__card">
      <div class="domain-picker-modal__head">
        <div>
          <h3>Attach an owned domain</h3>
          <div class="domain-picker-modal__msub">{{ contextLabel || 'Step 3a · select from the owned-domain inventory' }}</div>
        </div>
        <button type="button" class="domain-picker-modal__close" @click="$emit('close')">&#10005;</button>
      </div>

      <div class="domain-picker-modal__search">
        <div class="domain-picker-modal__search-box">
          <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><circle cx="8" cy="8" r="5"/><path d="M12 12l4 4"/></svg>
          <input v-model="search" type="text" placeholder="Search owned domains…" />
        </div>
      </div>

      <div class="domain-picker-modal__filters">
        <span
          v-for="state in FILTER_ORDER"
          :key="state"
          class="domain-picker-modal__chip-toggle"
          :class="{
            'domain-picker-modal__chip-toggle--active': activeStates.has(state),
            'domain-picker-modal__chip-toggle--locked': !REUSABLE_STATES.includes(state),
          }"
          @click="toggleState(state)"
        >{{ stateLabel(state) }} ({{ stateCounts[state] || 0 }})</span>
      </div>

      <div class="domain-picker-modal__list">
        <div v-if="loading" class="domain-picker-modal__empty">Loading the inventory…</div>
        <div v-else-if="loadError" class="domain-picker-modal__empty domain-picker-modal__empty--error">{{ loadError }}</div>
        <div v-else-if="!filtered.length" class="domain-picker-modal__empty">No owned domains match.</div>
        <DomainRow
          v-for="domain in filtered"
          :key="domain.id"
          :domain="domain"
          density="picker"
          :selected="selectedId === domain.id"
          @select="selectDomain"
        />
      </div>

      <div class="domain-picker-modal__footer">
        <!-- Deliberately a bare text link, weaker than the primary Attach
             button — buying a domain is the fallback branch (§5.1 step 3a/3:
             the cheapest domain is one we already own). -->
        <button type="button" class="domain-picker-modal__link-btn" @click="$emit('register-new')">
          Register a new domain instead
          <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 9h10M9.5 4.5 14 9l-4.5 4.5"/></svg>
        </button>
        <button
          type="button"
          class="domain-picker-modal__attach-btn"
          :disabled="!selectedDomain"
          @click="$emit('attach', selectedDomain)"
        >{{ selectedDomain ? `Attach ${selectedDomain.domain} →` : 'Select a domain to attach' }}</button>
      </div>
    </div>
  </div>
</template>

<script setup>
// Step-3a "attach an owned domain" picker — `domains-register.html` §4, a
// modal variant of the same register (rows are the canonical DomainRow at
// `density="picker"`, per the design's one-row-component rule).
//
// Reusable, self-fetching component for the provisioning-run surface. M4
// ships the `domain.attach_existing` step that actually consumes the
// `attach` event; until then the register page mounts this behind
// `?picker=preview` so the surface stays reviewable.
//
// Filtered to Parked/Abandoned by default — the two states worth reusing.
// Unknown and In use stay SELECTABLE but visually locked (greyed toggles):
// usage_state is never trusted at attach time, so picking either simply
// routes through step 3b's occupancy check like everything else (§4.6a) —
// the modal never hard-disables them.
import { computed, onMounted, reactive, ref } from 'vue';
import useApi from '@apexl/icarus-core/src/services/api';
import DomainRow from '@root/components/Domains/DomainRow.vue';
import { daysUntil, stateLabel } from '@root/services/domainFormat';

const FILTER_ORDER = ['parked', 'abandoned', 'unknown', 'in_use'];
const REUSABLE_STATES = ['parked', 'abandoned'];

const props = defineProps({
  apiBase: { type: String, default: '/api/v1/domains' },
  contextLabel: { type: String, default: '' },
});

defineEmits(['close', 'attach', 'register-new']);

const api = useApi();

const domains = ref([]);
const loading = ref(false);
const loadError = ref(null);
const search = ref('');
const activeStates = reactive(new Set(REUSABLE_STATES));
const selectedId = ref(null);

const stateCounts = computed(() => {
  const counts = {};
  for (const domain of domains.value) {
    counts[domain.usage_state] = (counts[domain.usage_state] || 0) + 1;
  }
  return counts;
});

const filtered = computed(() => domains.value
  .filter((d) => activeStates.has(d.usage_state))
  .filter((d) => !search.value || d.domain.toLowerCase().includes(search.value.toLowerCase()))
  .sort((a, b) => (daysUntil(a.expires_at) ?? Infinity) - (daysUntil(b.expires_at) ?? Infinity)));

const selectedDomain = computed(() => domains.value.find((d) => d.id === selectedId.value) || null);

function toggleState(state) {
  if (activeStates.has(state)) {
    activeStates.delete(state);
  } else {
    activeStates.add(state);
  }
  if (selectedDomain.value && !activeStates.has(selectedDomain.value.usage_state)) {
    selectedId.value = null;
  }
}

function selectDomain(domain) {
  selectedId.value = selectedId.value === domain.id ? null : domain.id;
}

async function load() {
  loading.value = true;
  loadError.value = null;
  try {
    const data = await api.get(props.apiBase);
    domains.value = data.domains || [];
  } catch (err) {
    loadError.value = 'Could not load the owned-domain inventory.';
  } finally {
    loading.value = false;
  }
}

onMounted(load);
</script>

<style lang="scss">
.domain-picker-modal {
  position: fixed;
  inset: 0;
  background: rgba(14, 46, 48, .85);
  z-index: 1060;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 36px 24px;

  &__card {
    background: var(--theme-bg-card);
    border-radius: var(--theme-radius-lg, 10px);
    box-shadow: var(--theme-shadow-panel);
    width: 100%;
    max-width: 660px;
    max-height: 90vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  &__head {
    padding: 18px 22px;
    border-bottom: 1px solid var(--theme-border-soft);
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    flex-shrink: 0;

    h3 {
      margin: 0;
      font-size: 16px;
      font-weight: 600;
    }
  }

  &__msub {
    font-size: 11.5px;
    color: var(--theme-text-tertiary);
    margin-top: 3px;
  }

  &__close {
    width: 26px;
    height: 26px;
    border-radius: var(--theme-radius, 6px);
    border: 1px solid var(--theme-border);
    background: var(--theme-bg-card);
    color: var(--theme-text-tertiary);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    font-size: 11px;
    line-height: 1;
  }

  &__search {
    padding: 14px 22px 0;
    flex-shrink: 0;
  }

  &__search-box {
    display: flex;
    align-items: center;
    gap: 7px;
    border: 1px solid var(--theme-border-strong);
    border-radius: var(--theme-radius-sm, 3px);
    padding: 6px 10px;
    background: var(--theme-bg-content);

    svg {
      width: 13px;
      height: 13px;
      color: var(--theme-text-tertiary);
      flex-shrink: 0;
    }

    input {
      border: none;
      background: none;
      font-size: 12.5px;
      outline: none;
      color: var(--theme-text-primary);
      width: 100%;
    }
  }

  &__filters {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 22px 16px;
    flex-wrap: wrap;
    flex-shrink: 0;
  }

  &__chip-toggle {
    font-size: 11.5px;
    font-weight: 600;
    padding: 6px 12px;
    border-radius: 14px;
    border: 1.5px solid var(--theme-border-strong);
    color: var(--theme-text-secondary);
    cursor: pointer;
    background: var(--theme-bg-card);
    user-select: none;

    &--active {
      background: var(--theme-accent-light);
      border-color: var(--theme-accent-primary);
      color: var(--theme-accent-dark);
    }

    &--locked {
      opacity: .6;
    }
  }

  &__list {
    flex: 1;
    overflow-y: auto;
    border-top: 1px solid var(--theme-border-soft);
    min-height: 120px;
    max-height: 330px;
  }

  &__empty {
    padding: 28px 22px;
    text-align: center;
    font-size: 12.5px;
    color: var(--theme-text-tertiary);

    &--error {
      color: #a53232;
    }
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 22px;
    border-top: 1px solid var(--theme-border-soft);
    background: var(--theme-bg-content);
    gap: 12px;
    flex-wrap: wrap;
    flex-shrink: 0;
  }

  &__link-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12.5px;
    font-weight: 600;
    color: var(--theme-text-secondary);
    cursor: pointer;
    background: none;
    border: none;
    padding: 4px 0;

    svg {
      width: 12px;
      height: 12px;
    }
  }

  &__attach-btn {
    background: var(--theme-accent-primary);
    color: #fff;
    border: none;
    border-radius: var(--theme-radius, 6px);
    padding: 10px 18px;
    font-size: 12.5px;
    font-weight: 700;
    cursor: pointer;

    &:disabled {
      opacity: .55;
      cursor: default;
    }
  }
}
</style>
